import React, { useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { useSelector } from "react-redux";
import styled from "styled-components";
import { PRIMARY_LIGHT_GRAY } from "../../constants/color";
import { dataSelectors } from "../../ducks/data";
import { fileSelectors } from "../../ducks/file";
import { userSelectors } from "../../ducks/user";
import { CloseIcon } from "../atoms/icons/CloseIcon";

type Props = {
  filter: {
    fileName?: string;
    userId?: string;
    menuId?: number;
    templateId?: number;
    fromDate?: string;
    toDate?: string;
    status?: number;
  };
  total: number;
  onClick?: (item: { value: string | number | undefined; key: string }) => void;
};

export const FilterBadge: React.FC<Props> = ({ filter, total, onClick }) => {
  const users = useSelector(userSelectors.users);
  const templates = useSelector(fileSelectors.templates);
  const menus = useSelector(dataSelectors.menus);
  const registrationResults = useSelector(dataSelectors.registrationResults);

  const formContext = useFormContext();

  const { setValue } = formContext ?? {};

  const handleRemoveFilter = (item: {
    value: string | number | undefined;
    key: string;
  }) => {
    setValue?.(item.key, "");
    onClick?.(item);
  };

  const filterItems = useMemo(() => {
    const user = users?.find((user) => user.id === filter.userId);
    const template = templates.find(
      (template) => template.id === filter.templateId,
    );
    const menu = menus.find((menu) => menu.id === filter.menuId);
    const status = registrationResults.find(
      (result) => result.id === filter.status,
    );

    return [
      { name: user?.name, value: filter.userId, key: "userId" },
      { name: template?.name, value: filter.templateId, key: "templateId" },
      { name: menu?.name, value: filter.menuId, key: "menuId" },
      { name: status?.name, value: filter.status, key: "status" },
    ].filter((item) => item.name);
  }, [users, templates, menus, registrationResults, filter]);

  if (
    filterItems.length === 0 &&
    !filter.fromDate &&
    !filter.toDate &&
    !filter.fileName
  ) {
    return null;
  }

  return (
    <StyledFilterDisplayContainer>
      <StyleFilterTextResult>
        {filter.fileName
          ? `「${filter.fileName}」 の検索結果 (${total}件)`
          : `検索結果 (${total}件)`}
      </StyleFilterTextResult>

      {filterItems.map((item, index) => (
        <StyledFilterBadge key={index} onClick={() => handleRemoveFilter(item)}>
          <StyledFilterBadgeText>{item.name}</StyledFilterBadgeText>
          <StyledIconWrapper>
            <CloseIcon />
          </StyledIconWrapper>
        </StyledFilterBadge>
      ))}

      {(filter.fromDate || filter.toDate) && (
        <StyledFilterBadge
          onClick={() =>
            handleRemoveFilter({
              value: "",
              key: "dateRange",
            })
          }
        >
          <StyledFilterBadgeText>
            {filter.fromDate} ~ {filter.toDate}
          </StyledFilterBadgeText>
          <StyledIconWrapper>
            <CloseIcon />
          </StyledIconWrapper>
        </StyledFilterBadge>
      )}
    </StyledFilterDisplayContainer>
  );
};

const StyledFilterDisplayContainer = styled.div`
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  padding: 24px 8px 8px 8px;
  gap: 8px;
`;

const StyledFilterBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  border-radius: 30px;
  padding: 5px 10px;
  cursor: pointer;
  background: ${PRIMARY_LIGHT_GRAY};
`;

const StyledFilterBadgeText = styled.div`
  font-weight: 400;
  font-size: 12px;
  line-height: 12px;
  letter-spacing: 0px;
  color: #333333;
`;

const StyledIconWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  svg {
    width: 10px;
    height: 10px;
  }
`;

const StyleFilterTextResult = styled.div`
  font-family: Noto Sans JP;
  font-weight: 400;
  font-size: 14px;
  line-height: 14px;
  letter-spacing: 0px;
  color: #333333;
`;
